import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "../styles/Contact.css";
import { getCsrfToken } from "../services/authService";

export default function Contact() {
  const { t } = useTranslation();

  const [form, setForm] = useState({
    name: "",
    email: "",
    message: "",
    privacy: false
  });

  const [errors, setErrors] = useState({
    name: "",
    email: "",
    message: "",
    privacy: ""
  });

  const [status, setStatus] = useState("");

  useEffect(() => {
    document.title = `${t("contact.helmetTitle")} - Alfasol`;
  }, [t]);

  const validateField = (name, value) => {
    switch (name) {
      case "name":
        return value.trim() ? "" : t("contact.validation.name");
      case "email":
        return /^\S+@\S+\.\S+$/.test(value) ? "" : t("contact.validation.email");
      case "message":
        return value.trim() ? "" : t("contact.validation.message");
      case "privacy":
        return value ? "" : t("contact.validation.privacy");
      default:
        return "";
    }
  };

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    const fieldValue = type === "checkbox" ? checked : value;

    setForm({ ...form, [name]: fieldValue });
    setErrors({ ...errors, [name]: validateField(name, fieldValue) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {
      name: validateField("name", form.name),
      email: validateField("email", form.email),
      message: validateField("message", form.message),
      privacy: validateField("privacy", form.privacy)
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some((error) => error !== "")) return;

    try {
      const csrf = await getCsrfToken();

      const response = await fetch("/contact", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          [csrf.headerName]: csrf.token,
        },
        body: JSON.stringify(form),
      });

      if (response.ok) {
        setStatus("success");
        setForm({ name: "", email: "", message: "", privacy: false });
      } else {
        setStatus("error");
      }
    } catch (error) {
      setStatus("error");
    }
  };

  return (
    <section className="section contact-section" id="contact">
      <div className="container">
        <h2 className="center">{t("contact.title")}</h2>
        <p className="center">{t("contact.subtitle")}</p>

        {status === "success" && (
          <p className="form-status success">{t("contact.success")}</p>
        )}

        {status === "error" && (
          <p className="form-status error">{t("contact.error")}</p>
        )}
        
        <form className="contact-form" onSubmit={handleSubmit}>
          
          <div className="form-group">
            <label>{t("contact.name")}</label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder={t("contact.namePlaceholder")}
              className={errors.name ? "error" : ""}
              required
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label>{t("contact.email")}</label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder={t("contact.emailPlaceholder")}
              className={errors.email ? "error" : ""}
              required
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label>{t("contact.message")}</label>
            <textarea
              name="message"
              value={form.message}
              onChange={handleChange}
              placeholder={t("contact.messagePlaceholder")}
              className={errors.message ? "error" : ""}
              required
            ></textarea>
            {errors.message && <span className="error-message">{errors.message}</span>}
          </div>

          <div className="form-group privacy-group">
            <input
              type="checkbox"
              id="privacy"
              name="privacy"
              checked={form.privacy}
              onChange={handleChange}
            />
            <label htmlFor="privacy">
              {t("contact.acceptPrivacy")}{" "}
              <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
                {t("contact.readPrivacy")}
              </a>
            </label>
            {errors.privacy && <span className="error-message">{errors.privacy}</span>}
          </div>

          {/* honeypot anti-spam */}
          <input type="text" name="company" style={{ display: "none" }} />

          <button type="submit" className="primary-btn">
            {t("contact.send")}
          </button>
        </form>

        <div className="legal-info">
          <h3>{t("contact.legal.title")}</h3>
          <p><strong>{t("contact.legal.responsable")}:</strong> {t("contact.legal.responsableName")}</p>
          <p><strong>{t("contact.legal.finalidad")}:</strong> {t("contact.legal.finalidadDesc")}</p>
          <p><strong>{t("contact.legal.legitimacion")}:</strong> {t("contact.legal.legitimacionDesc")}</p>
          <p><strong>{t("contact.legal.destinatario")}:</strong> {t("contact.legal.destinatarioDesc")}</p>
          <p><strong>{t("contact.legal.derechos")}:</strong> {t("contact.legal.derechosDesc")} </p>
          <p>
            <strong>{t("contact.legal.otroEmail")}:</strong> <a href="mailto:info@alfasol.es">info@alfasol.es</a>
          </p>
        </div>
      </div>
    </section>
  );
}